import clsx from 'clsx'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import s from './styles.module.scss'

import { BillingColumns } from '../../details-container/BillingColumns'
import { DescriptionMode } from '../../details-container/DescriptionMode'
import { useLocale } from '../../../../../../hooks/use-locale'
import {
  ClientPricingItemAdjustmentStatusEnum,
  SingleLinePriceClientPricingItemFragment,
} from '../../../../../../lib/graphql'
import { provisionUnitToLabel } from '../../../../../../utils'
import {
  decimalPrice,
  percentage,
} from '../../../../../../utils/currencyFormatter'
import { Icon } from '../../../../../atoms'
import { useCurrencyAreaContext } from '../../../../../contexts'
import SeasonTag from '../../../../../templates/house-options/shared/components/seasonal-busyness-calendar/components/tag/season-tag/SeasonTag'

export type SingleLinePriceClientPricingItem = Omit<
  SingleLinePriceClientPricingItemFragment,
  'objectData'
> & {
  objectData: Omit<
    SingleLinePriceClientPricingItemFragment['objectData'],
    'label'
  > & {
    label: string | React.ReactNode
  }
}

export const ColumnValue = ({
  value,
  valueWithDiscount,
  isDiscount = false,
  className,
  displayPrice = true,
}: {
  value: string
  valueWithDiscount?: string
  isDiscount?: boolean
  className?: string
  displayPrice?: boolean
}) => (
  <div
    className={clsx(s.itemPrice, className, {
      [s.itemPriceModal]: !displayPrice,
    })}
  >
    <span className={clsx({ [s.isDiscount]: isDiscount })}>{value}</span>
    {isDiscount && valueWithDiscount && (
      <span className={s.discountItem}>{valueWithDiscount}</span>
    )}
  </div>
)

const SingleLinePrice = ({
  className,
  columns,
  displayMode = DescriptionMode.hidden,
  total = {
    isTotal: false,
    isTotalPerAdult: false,
  },
  showAdjustments = false,
  children,
  emptyDate = false,
  clientPricingItem,
  onClick,
  displayPrice = true,
}: {
  className?: string
  displayMode?: DescriptionMode
  date?: Date
  columns: BillingColumns[]
  total?: {
    isTotal: boolean
    isTotalPerAdult: boolean
  }
  showAdjustments?: boolean
  children?: React.ReactNode
  emptyDate?: boolean
  clientPricingItem: SingleLinePriceClientPricingItem
  onClick?: (clientPricingItem: SingleLinePriceClientPricingItem) => void
  displayPrice?: boolean
}) => {
  const intl = useIntl()
  const { locale } = useLocale()
  const { currency } = useCurrencyAreaContext()
  const style = {}
  const date = null
  const [displayMore, setDisplayMore] = useState(
    displayMode === DescriptionMode.visible,
  )
  const isDiscount = useMemo(
    () =>
      (clientPricingItem.unitPrice?.priceWithDiscount &&
        clientPricingItem.unitPrice?.basePrice?.priceWithoutVat !==
          clientPricingItem.unitPrice?.priceWithDiscount?.priceWithoutVat) ||
      (clientPricingItem.totalPrice?.priceWithDiscount &&
        clientPricingItem.totalPrice?.basePrice?.priceWithoutVat !==
          clientPricingItem.totalPrice?.priceWithDiscount?.priceWithoutVat)
        ? true
        : false,
    [clientPricingItem],
  )

  const canDisplayDescription =
    clientPricingItem.objectData?.description ||
    (clientPricingItem.objectData?.nestedItemsObjectData?.length ?? 0) > 0

  const getTitleToDisplay = () => {
    return (
      <div className={s.name} style={{ flex: `${descriptionFlexSize} 1` }}>
        <span
          className={clsx(s.title, { [s.season]: clientPricingItem.season })}
        >
          {clientPricingItem.objectData?.label}
          {clientPricingItem.season && (
            <SeasonTag season={clientPricingItem.season} />
          )}
        </span>
      </div>
    )
  }
  const descriptionFlexSize =
    (emptyDate ? 2 : 0) + (columns.includes(BillingColumns.unit) ? 7 : 10)

  return (
    <div
      className={clsx(s.container, className, {
        [s.descriptionModeToggable]:
          canDisplayDescription && displayMode === DescriptionMode.toggable,
        [s.total]: total?.isTotal,
        [s.totalPerAdult]: total?.isTotalPerAdult,
        [s.addedVariant]:
          clientPricingItem.adjustmentStatus ===
          ClientPricingItemAdjustmentStatusEnum.ADDED,
        [s.removedVariant]:
          clientPricingItem.adjustmentStatus ===
          ClientPricingItemAdjustmentStatusEnum.REMOVED,
      })}
      style={{ breakInside: 'avoid', ...style }}
      onClick={() => {
        if (onClick) {
          onClick(clientPricingItem)
        } else {
          setDisplayMore((prev) => !prev)
        }
      }}
    >
      <div className={s.billingTableDetails}>
        <div style={{ display: 'flex', flex: 1, width: '100%' }}>
          {showAdjustments && (
            <div className={s.icon}>
              {clientPricingItem.adjustmentStatus ===
              ClientPricingItemAdjustmentStatusEnum.ADDED
                ? '+'
                : '-'}
            </div>
          )}

          {columns.includes(BillingColumns.date) && (
            <span className={s.date}>
              {date ? dayjs(date).format('DD/MM') : '-'}
            </span>
          )}
          {columns.includes(BillingColumns.description) && getTitleToDisplay()}
          {columns.includes(BillingColumns.ownerFeesRate) && (
            <ColumnValue
              value={
                clientPricingItem.priceOption?.fees?.ownerFeesRate !== undefined
                  ? percentage({
                      rate: clientPricingItem.priceOption?.fees?.ownerFeesRate,
                      locale,
                    })
                  : ''
              }
              valueWithDiscount={
                clientPricingItem.priceOption?.fees?.ownerFeesRate !== undefined
                  ? percentage({
                      rate:
                        clientPricingItem.priceOption?.fees?.ownerFeesRate ?? 0,
                      locale,
                    })
                  : ''
              }
              isDiscount={isDiscount}
              className={s.vat}
              displayPrice={displayPrice}
            />
          )}
          {columns.includes(BillingColumns.unit) && (
            <ColumnValue
              value={
                clientPricingItem.priceUnit
                  ? provisionUnitToLabel({
                      unit: clientPricingItem.priceUnit,
                      intl,
                    })
                  : ''
              }
              className={clsx(s.price)}
              displayPrice={displayPrice}
            />
          )}
          {columns.includes(BillingColumns.quantity) && (
            <ColumnValue
              value={clientPricingItem.priceOption?.quantity?.toString() ?? '-'}
              valueWithDiscount={`${clientPricingItem.priceOption?.quantity?.toString() ?? '-'}`}
              isDiscount={isDiscount}
              className={s.quantity}
              displayPrice={displayPrice}
            />
          )}
          {columns.includes(BillingColumns.unitHT) && (
            <ColumnValue
              value={decimalPrice(
                clientPricingItem.unitPrice?.basePrice?.priceWithoutVat,
                {
                  currency,
                },
              )}
              valueWithDiscount={decimalPrice(
                clientPricingItem.unitPrice?.priceWithDiscount?.priceWithoutVat,
                {
                  currency,
                },
              )}
              isDiscount={isDiscount}
              className={clsx(s.price, s.unitHT)}
              displayPrice={displayPrice}
            />
          )}
          {columns.includes(BillingColumns.totalHT) && (
            <ColumnValue
              value={decimalPrice(
                clientPricingItem?.totalPrice?.basePrice?.priceWithoutVat,
                {
                  currency,
                },
              )}
              valueWithDiscount={decimalPrice(
                clientPricingItem?.totalPrice?.priceWithDiscount
                  ?.priceWithoutVat,
                {
                  currency,
                },
              )}
              isDiscount={isDiscount}
              className={s.price}
              displayPrice={displayPrice}
            />
          )}
          {columns.includes(BillingColumns.vatRate) && (
            <ColumnValue
              value={percentage({
                rate: total?.isTotal
                  ? undefined
                  : clientPricingItem.unitPrice?.basePrice?.vatRate ||
                    undefined,
                locale,
              })}
              valueWithDiscount={percentage({
                rate:
                  clientPricingItem?.totalPrice?.priceWithDiscount?.vatRate ||
                  undefined,
                locale,
              })}
              isDiscount={isDiscount}
              className={clsx(s.vat, s.vatRate)}
              displayPrice={displayPrice}
            />
          )}
          {columns.includes(BillingColumns.vat) && (
            <ColumnValue
              value={decimalPrice(
                clientPricingItem?.totalPrice?.basePrice?.vat,
                { currency },
              )}
              valueWithDiscount={decimalPrice(
                clientPricingItem?.totalPrice?.priceWithDiscount?.vat,
                {
                  currency,
                },
              )}
              isDiscount={isDiscount}
              className={s.vat}
              displayPrice={displayPrice}
            />
          )}
          {columns.includes(BillingColumns.unitTTC) && (
            <ColumnValue
              value={decimalPrice(
                clientPricingItem.unitPrice?.basePrice?.priceWithVat,
                {
                  currency,
                },
              )}
              valueWithDiscount={decimalPrice(
                clientPricingItem.unitPrice?.priceWithDiscount?.priceWithVat,
                {
                  currency,
                },
              )}
              isDiscount={isDiscount}
              className={clsx(s.price)}
              displayPrice={displayPrice}
            />
          )}
          {columns.includes(BillingColumns.totalTTC) && (
            <ColumnValue
              value={decimalPrice(
                clientPricingItem?.totalPrice?.basePrice?.priceWithVat,
                {
                  currency,
                },
              )}
              valueWithDiscount={decimalPrice(
                clientPricingItem?.totalPrice?.priceWithDiscount?.priceWithVat,
                {
                  currency,
                },
              )}
              isDiscount={isDiscount}
              className={s.price}
              displayPrice={displayPrice}
            />
          )}
        </div>
        {displayMode === DescriptionMode.toggable && (
          <div className={s.containerArrow}>
            {(canDisplayDescription || children) && (
              <Icon
                name='arrow-down'
                size={12}
                className={clsx(s.arrow, { [s.arrowUp]: displayMore })}
                color='#8D8D8E'
              />
            )}
          </div>
        )}
      </div>
      {canDisplayDescription && displayMore && (
        <div
          className={clsx(
            { [s.visibleDescription]: displayMode === DescriptionMode.visible },
            s.description,
          )}
        >
          {clientPricingItem.objectData?.description &&
            parse(clientPricingItem.objectData?.description)}
          {(clientPricingItem.objectData?.nestedItemsObjectData?.length ?? 0) >
            0 && (
            <div className={s.nestedItems}>
              {(clientPricingItem.objectData?.nestedItemsObjectData ?? []).map(
                (nestedItem) => (
                  <div
                    key={nestedItem.objectId ?? nestedItem.type}
                    className={s.nestedItem}
                  >
                    {nestedItem.label}
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      )}
      {children && displayMore && <div className={s.children}>{children}</div>}
    </div>
  )
}

export default SingleLinePrice
